import React, { useState } from 'react';
import { BACKEND } from '../../consts/backend';
import { Icon } from '../Component';

const CompanySelector = ({ isOpen, onCompanySelected }) => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const companies = [
    {
      id: 'dow',
      name: 'DOW',
      database: 'ssb_internacional',
      description: 'Dow Chemical Company',
      color: '#e74c3c',
      logo: '🏭'
    },
    {
      id: 'bostik',
      name: 'Bostik',
      database: 'ssb_internacional_bostik', // -> utilizar esta cuando este
      description: 'Bostik Adhesives',
      color: '#2980b9',
      logo: '🔧'
    }
  ];

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setError('');
  };

  const handleConfirm = async () => {
    if (!selectedCompany) {
      setError('Por favor selecciona una empresa');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND}/db/set/${selectedCompany.database}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Para enviar cookies de sesión
      });

      const data = await response.json();

      if (data.success) {
        // Guardar información de la empresa en localStorage
        localStorage.setItem('selectedCompany', JSON.stringify(selectedCompany));
        localStorage.setItem('currentDatabase', selectedCompany.database);
        
        onCompanySelected(selectedCompany);
      } else {
        setError(data.message || 'Error configurando la empresa');
      }
    } catch (err) {
      setError('Error de conexión con el servidor');
      console.error('Error setting company:', err);
    } finally {
      setLoading(false);
    }
  };

  // No renderizar si no está abierto
  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" style={styles.backdrop}>
      <div className="modal-content" style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Selecciona tu Empresa</h2>
          <p style={styles.subtitle}>
            Elige la empresa con la que trabajarás en esta sesión
          </p>
        </div>

        <div style={styles.companiesGrid}>
          {companies.map((company) => (
            <div
              key={company.id}
              className={`company-card ${selectedCompany?.id === company.id ? 'selected' : ''}`}
              style={{
                ...styles.companyCard,
                ...(selectedCompany?.id === company.id ? styles.selectedCard : {}),
                borderColor: company.color
              }}
              onClick={() => handleCompanySelect(company)}
            >
              <div style={styles.companyLogo}>{company.logo}</div>
              <h3 style={{ ...styles.companyName, color: company.color }}>
                {company.name}
              </h3>
              <p style={styles.companyDescription}>{company.description}</p>
              <small style={styles.databaseInfo}>BD: {company.database}</small>
            </div>
          ))}
        </div>

        {error && (
          <div style={styles.error}>
            <Icon name="alert-circle" style={styles.errorIcon} />
            {error}
          </div>
        )}

        <div style={styles.footer}>
          <button
            onClick={handleConfirm}
            disabled={!selectedCompany || loading}
            style={{
              ...styles.confirmButton,
              backgroundColor: selectedCompany?.color || '#ddd'
            }}
          >
            {loading ? (
              <>
                <span style={styles.spinner}></span>
                Configurando...
              </>
            ) : (
              'Continuar'
            )}
          </button>
        </div>

        <div style={styles.warningBox}>
          <Icon name="info" style={styles.warningIcon} />
          <span>
            La selección de empresa determinará a qué base de datos accederás.
            Puedes cambiar esto desde el menú de usuario.
          </span>
        </div>
      </div>
    </div>
  );
};

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '32px',
    width: '90%',
    maxWidth: '500px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    animation: 'modalSlideIn 0.3s ease-out'
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#2c3e50',
    margin: '0 0 8px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#7f8c8d',
    margin: 0
  },
  companiesGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '24px'
  },
  companyCard: {
    border: '2px solid #ecf0f1',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#fafafa'
  },
  selectedCard: {
    backgroundColor: '#f8f9fa',
    transform: 'scale(1.02)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
  },
  companyLogo: {
    fontSize: '32px',
    marginBottom: '8px'
  },
  companyName: {
    fontSize: '18px',
    fontWeight: 'bold',
    margin: '0 0 4px 0'
  },
  companyDescription: {
    fontSize: '12px',
    color: '#7f8c8d',
    margin: '0 0 8px 0'
  },
  databaseInfo: {
    fontSize: '10px',
    color: '#95a5a6',
    fontFamily: 'monospace'
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px'
  },
  confirmButton: {
    padding: '12px 32px',
    borderRadius: '6px',
    border: 'none',
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'opacity 0.2s ease'
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  error: {
    backgroundColor: '#fee',
    color: '#c33',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px'
  },
  errorIcon: {
    fontSize: '16px'
  },
  warningBox: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    padding: '12px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontSize: '12px',
    color: '#6c757d'
  },
  warningIcon: {
    fontSize: '14px',
    color: '#6c757d',
    marginTop: '1px'
  }
};

export default CompanySelector;
